import {
    ActivateAwaitingUserParams,
    DeactivateActiveUserParams,
    DeleteAwaitingUserParams,
    ActivateCityParams,
    ActivateVoiceParams,
    AddCityCompanyParams,
    AddTrackCompanyParams,
    CreateCityParams,
    CreateCompanyParams,
    CreatePromptParams,
    DeleteCityParams,
    DeleteCompanyParams,
    GetPromptsParams,
    UpdateCompanyLimitParams,
    UpdateCompanyAddonsParams,
    UpdatePromptParams,
} from "../types/requestInputs";
import {
    GetBusinessCatalogsResponse,
    GetFiltersResponse,
    GetLanguagesAIResponse,
    GetPromptsResponse,
} from "../types/requestsOutputs";
import { City, Company, Country, MessageResponse, TrackIds, User, VoiceAI } from "../types/types";
import { fetchAPI } from "./utils";

export const getPrompts = async ({ token }: GetPromptsParams): Promise<GetPromptsResponse> => {
    return await fetchAPI<GetPromptsResponse>("/get-prompt-ai", "GET", undefined, token);
};

export const createPrompt = async ({
    token,
    id,
    internalName,
    prompt,
}: CreatePromptParams): Promise<MessageResponse> => {
    return fetchAPI<MessageResponse>("/create-prompt-ai", "POST", { id, internalName, prompt }, token);
};

export const updatePrompt = async ({
    token,
    id,
    internalName,
    prompt,
}: UpdatePromptParams): Promise<MessageResponse> => {
    return fetchAPI<MessageResponse>("/update-prompt-ai", "POST", { id, internalName, prompt }, token);
};

export const getCitiesAdmin = async (token: string, language: string): Promise<City[]> => {
    return await fetchAPI<City[]>("/get-cities-admin", "GET", undefined, token, language);
};

export const createCityAdmin = async ({ token, cityName, countryId }: CreateCityParams): Promise<MessageResponse> => {
    return fetchAPI<MessageResponse>("/create-city", "POST", { countryId, cityname: cityName }, token);
};

export const deleteCityAdmin = async ({ token, cityId }: DeleteCityParams): Promise<MessageResponse> => {
    return fetchAPI<MessageResponse>("/delete-city", "POST", { cityId }, token);
};

export const getCountriesAdmin = async (token: string, language: string): Promise<Country[]> => {
    return await fetchAPI<City[]>("/get-countries-admin", "GET", undefined, token, language);
};

export const getCompaniesAdmin = async (token: string): Promise<Company[]> => {
    return await fetchAPI<Company[]>("/get-companies-admin", "GET", undefined, token);
};

export const createCompanyAdmin = async ({ token, companyName }: CreateCompanyParams): Promise<MessageResponse> => {
    return fetchAPI<MessageResponse>("/create-company", "POST", { companyName }, token);
};

export const deleteCompanyAdmin = async ({ token, companyId }: DeleteCompanyParams): Promise<MessageResponse> => {
    return fetchAPI<MessageResponse>("/delete-company", "POST", { companyId }, token);
};

export const addTrackToCompany = async ({
    token,
    companyId,
    trackId,
}: AddTrackCompanyParams): Promise<MessageResponse> => {
    return fetchAPI<MessageResponse>("/add-track-to-company", "POST", { companyId, trackId }, token);
};

export const removeTrackFromCompany = async ({
    token,
    companyId,
    trackId,
}: AddTrackCompanyParams): Promise<MessageResponse> => {
    return fetchAPI<MessageResponse>("/remove-track-from-company", "POST", { companyId, trackId }, token);
};

export const addCityFromCompany = async ({
    token,
    companyId,
    cityId,
}: AddCityCompanyParams): Promise<MessageResponse> => {
    return fetchAPI<MessageResponse>("/add-city-to-company", "POST", { companyId, cityId }, token);
};

export const removeCityFromCompany = async ({
    token,
    companyId,
    cityId,
}: AddCityCompanyParams): Promise<MessageResponse> => {
    return fetchAPI<MessageResponse>("/remove-city-from-company", "POST", { companyId, cityId }, token);
};

export const getTrackIds = async (token: string): Promise<TrackIds[]> => {
    return fetchAPI<TrackIds[]>("/get-track-ids", "GET", undefined, token);
};

export type WalkingTourMode = "guided" | "free";

export interface WalkingTourTrackItem {
    id: string;
    name: string | null;
    currentMode: WalkingTourMode | null;
}

export interface WalkingTourTracksResponse {
    bothFiltersExist: boolean;
    tracks: WalkingTourTrackItem[];
}

export interface TrackWalkingModeResponse {
    trackId: string;
    internalName: string | null;
    currentMode: WalkingTourMode | null;
    isEligible: boolean;
    bothFiltersExist: boolean;
    isHOHO?: boolean;
    trackHohoId?: string | null;
}

export interface SwitchTrackWalkingModeResponse {
    trackId: string;
    newMode: WalkingTourMode;
    trackHohoId: string | null;
}

export const getWalkingTourTracks = async ({ token }: { token: string }): Promise<WalkingTourTracksResponse> => {
    return fetchAPI<WalkingTourTracksResponse>("/get-walking-tour-tracks", "POST", {}, token);
};

export const getTrackWalkingMode = async ({
    token,
    trackId,
}: {
    token: string;
    trackId: string;
}): Promise<TrackWalkingModeResponse> => {
    return fetchAPI<TrackWalkingModeResponse>("/get-track-walking-mode", "POST", { trackId }, token);
};

export const switchTrackWalkingMode = async ({
    token,
    trackId,
    mode,
}: {
    token: string;
    trackId: string;
    mode: WalkingTourMode;
}): Promise<SwitchTrackWalkingModeResponse> => {
    return fetchAPI<SwitchTrackWalkingModeResponse>("/switch-track-walking-mode", "POST", { trackId, mode }, token);
};

export const updateCompanyTextLimit = async ({
    token,
    companyId,
    limit,
}: UpdateCompanyLimitParams): Promise<MessageResponse> => {
    return fetchAPI<MessageResponse>("/update-text-limit", "POST", { companyId, limit }, token);
};

export const updateCompanyAudioLimit = async ({
    token,
    companyId,
    limit,
}: UpdateCompanyLimitParams): Promise<MessageResponse> => {
    return fetchAPI<MessageResponse>("/update-audio-limit", "POST", { companyId, limit }, token);
};

export const updateCompanyAddons = async ({
    token,
    companyId,
    addons,
}: UpdateCompanyAddonsParams): Promise<MessageResponse> => {
    return fetchAPI<MessageResponse>("/update-company-addons", "POST", { companyId, addons }, token);
};

export const getAwaitingUsersAdmin = async (token: string): Promise<User[]> => {
    return fetchAPI<User[]>("/get-awaiting-users", "GET", undefined, token);
};

export const getActiveUsersAdmin = async (token: string): Promise<User[]> => {
    return fetchAPI<User[]>("/get-active-users", "GET", undefined, token);
};

export const activateAwaitingUserAdmin = async ({
    token,
    userIdToActivate,
}: ActivateAwaitingUserParams): Promise<MessageResponse> => {
    return fetchAPI<MessageResponse>("/activate-awaiting-user", "POST", { userIdToActivate }, token);
};

export const deactivateActiveUserAdmin = async ({
    token,
    userIdToDeactivate,
}: DeactivateActiveUserParams): Promise<MessageResponse> => {
    return fetchAPI<MessageResponse>("/deactivate-active-user", "POST", { userIdToDeactivate }, token);
};

export const deleteAwaitingUserAdmin = async ({
    token,
    userIdToDelete,
}: DeleteAwaitingUserParams): Promise<MessageResponse> => {
    return fetchAPI<MessageResponse>("/delete-awaiting-user", "POST", { userIdToDelete }, token);
};

export const activateCityAdmin = async ({ token, cityId }: ActivateCityParams): Promise<MessageResponse> => {
    return fetchAPI<MessageResponse>("/activate-city", "POST", { cityIdToActivate: cityId }, token);
};

export const deactivateCityAdmin = async ({ token, cityId }: ActivateCityParams): Promise<MessageResponse> => {
    return fetchAPI<MessageResponse>("/deactivate-city", "POST", { cityIdToDeactivate: cityId }, token);
};

export const activateCityAdminPWA = async ({ token, cityId }: ActivateCityParams): Promise<MessageResponse> => {
    return fetchAPI<MessageResponse>("/activate-city-pwa", "POST", { cityIdToActivate: cityId }, token);
};

export const deactivateCityAdminPWA = async ({ token, cityId }: ActivateCityParams): Promise<MessageResponse> => {
    return fetchAPI<MessageResponse>("/deactivate-city-pwa", "POST", { cityIdToDeactivate: cityId }, token);
};

export const getVoicesAdmin = async (token: string, language: string): Promise<VoiceAI[]> => {
    return await fetchAPI<VoiceAI[]>("/get-voices-admin", "GET", undefined, token, language);
};

export const activateVoiceAdmin = async ({ token, voiceId }: ActivateVoiceParams): Promise<MessageResponse> => {
    return fetchAPI<MessageResponse>("/activate-voice", "POST", { voiceId }, token);
};

export const deactivateVoiceAdmin = async ({ token, voiceId }: ActivateVoiceParams): Promise<MessageResponse> => {
    return fetchAPI<MessageResponse>("/deactivate-voice", "POST", { voiceId }, token);
};

export interface StaleVoice {
    id: string;
    name: string;
    language: string;
    isActive: boolean;
}

export interface DisabledVoice {
    id: string;
    name: string;
    language: string;
    isActive: boolean;
}

export interface RefreshVoicesResponse {
    message: string;
    staleVoices: StaleVoice[];
    disabledVoices: DisabledVoice[];
}

export const deactivateVoices = async (token: string, voiceIds: string[]): Promise<MessageResponse> => {
    return await fetchAPI<MessageResponse>("/deactivate-voices", "POST", { voiceIds }, token);
};

export const refreshVoicesAdmin = async (token: string): Promise<RefreshVoicesResponse> => {
    return await fetchAPI<RefreshVoicesResponse>("/refresh-voices", "GET", undefined, token);
};

export const removeStaleVoices = async (token: string, voiceIds: string[]): Promise<MessageResponse> => {
    return await fetchAPI<MessageResponse>("/remove-stale-voices", "POST", { voiceIds }, token);
};

export const createBusinessCatalog = async (
    token: string,
    businessCatalogName: string,
    cityId: string,
    numberTracks: number,
    nominatifs: boolean,
): Promise<MessageResponse> => {
    return fetchAPI<MessageResponse>(
        "/create-business-catalog",
        "POST",
        { businessCatalogName, cityId, numberTracks, nominatifs },
        token,
    );
};

export const updateBusinessCatalog = async (
    token: string,
    businessCatalogId: string,
    updates: any,
): Promise<MessageResponse> => {
    return fetchAPI<MessageResponse>("/update-business-catalog", "POST", { businessCatalogId, updates }, token);
};

export const deleteBusinessCatalog = async (token: string, businessCatalogId: string): Promise<MessageResponse> => {
    return fetchAPI<MessageResponse>("/delete-business-catalog", "POST", { businessCatalogId }, token);
};

export const createFilter = async (
    token: string,
    filterName: string,
    logoUrl: string,
    hasOnlyGardenStories: boolean,
    isDrivingMaps: boolean,
    categoryId: string,
): Promise<MessageResponse> => {
    return fetchAPI<MessageResponse>(
        "/create-filter",
        "POST",
        { filterName, logoUrl, hasOnlyGardenStories, isDrivingMaps, categoryId },
        token,
    );
};

export interface FilterUpdatableFields {
    logoUrl?: string;
    hasOnlyGardenStories?: boolean;
    isDrivingMaps?: boolean;
    status?: boolean;
    internalName?: string;
    categoryId?: string;
}

export const updateFilter = async (
    token: string,
    filterId: string,
    updates: FilterUpdatableFields,
): Promise<MessageResponse> => {
    return fetchAPI<MessageResponse>("/update-filter", "POST", { filterId, updates }, token);
};

export const deleteFilter = async (token: string, filterId: string): Promise<MessageResponse> => {
    return fetchAPI<MessageResponse>("/delete-filter", "POST", { filterId }, token);
};

export const createLanguage = async (
    token: string,
    languagesAIName: string,
    languagesAICode: string,
): Promise<MessageResponse> => {
    return fetchAPI<MessageResponse>("/create-language", "POST", { languagesAIName, languagesAICode }, token);
};

export const updateLanguage = async (
    token: string,
    languagesAIId: string,
    updates: any[],
): Promise<MessageResponse> => {
    return fetchAPI<MessageResponse>("/update-language", "POST", { languagesAIId, updates }, token);
};

export const deleteLanguage = async (token: string, languagesAIId: string): Promise<MessageResponse> => {
    return fetchAPI<MessageResponse>("/delete-language", "POST", { languagesAIId }, token);
};

export const addTrackBusinessCatalog = async (
    token: string,
    businessCatalogId: string,
    trackId: string,
): Promise<MessageResponse> => {
    return fetchAPI<MessageResponse>("/add-track-business-catalog", "POST", { businessCatalogId, trackId }, token);
};

export const removeTrackBusinessCatalog = async (
    token: string,
    businessCatalogId: string,
    trackId: string,
): Promise<MessageResponse> => {
    return fetchAPI<MessageResponse>("/remove-track-business-catalog", "POST", { businessCatalogId, trackId }, token);
};

export const getBusinessCatalogs = async (token: string): Promise<GetBusinessCatalogsResponse> => {
    return fetchAPI<GetBusinessCatalogsResponse>("/get-business-catalogs", "POST", {}, token);
};

export const getFilters = async (token: string): Promise<GetFiltersResponse> => {
    return fetchAPI<GetFiltersResponse>("/get-filters", "POST", {}, token);
};

export const getLanguages = async (token: string): Promise<GetLanguagesAIResponse> => {
    return fetchAPI<GetLanguagesAIResponse>("/get-languages", "POST", {}, token);
};

export const getCompaniesByDatabase = async (
    token: string,
    database: string,
): Promise<{ id: string; name: string }[]> => {
    return fetchAPI<{ id: string; name: string }[]>("/get-companies-by-database", "POST", { database }, token);
};

export const getApiKeys = async (token: string): Promise<ApiKeyListItem[]> => {
    return fetchAPI<ApiKeyListItem[]>("/get-api-keys", "POST", {}, token);
};

export const createApiKeyRequest = async (
    token: string,
    name: string,
    database: string,
    companyId: string,
    serviceUserId: string,
): Promise<{ key: string; id: string }> => {
    return fetchAPI<{ key: string; id: string }>(
        "/create-api-key",
        "POST",
        { name, database, companyId, serviceUserId },
        token,
    );
};

export const getAdminsByCompany = async (
    token: string,
    database: string,
    companyId: string,
): Promise<{ id: string; email: string; username: string | null }[]> => {
    return fetchAPI<{ id: string; email: string; username: string | null }[]>(
        "/get-admins-by-company",
        "POST",
        { database, companyId },
        token,
    );
};

export const toggleApiKeyRequest = async (token: string, apiKeyId: string): Promise<MessageResponse> => {
    return fetchAPI<MessageResponse>("/toggle-api-key", "POST", { apiKeyId }, token);
};

export const deleteApiKeyRequest = async (token: string, apiKeyId: string): Promise<MessageResponse> => {
    return fetchAPI<MessageResponse>("/delete-api-key", "POST", { apiKeyId }, token);
};

export interface ApiKeyListItem {
    id: string;
    name: string;
    companyName: string;
    companyId: string;
    database: string;
    maskedKey: string;
    isActive: boolean;
    requestCount: number;
    lastUsedAt: string | null;
    expiresAt: string | null;
    createdAt: string;
}
